import React, { useState, useEffect } from "react";
import AvailabilityCalendar from "./AvailabilityCalendar";
import { ServicePricingSelector } from "./BookingFlowPanels";
import { resolveServicePricing } from "../utils/servicePricing";
import "./InlineBookingWidget.css";
import {
  getApiBaseUrl,
  getAuthHeaders,
  getOrCreateSessionId,
} from "../utils/authUtils";
import { formatProfessionalType } from "../utils/formatters";

/**
 * InlineBookingWidget Component
 * Quick booking widget for checkout page - minimal fields, fast booking
 * Used when customer has products but no service appointment
 *
 * Phase C11.5: Simplified auth - receives auth state from parent CheckoutPage
 *
 * Props:
 * - shopId: Required - Shop location ID
 * - shopName: Required - Shop name for display
 * - bookingProductIds: Optional - Shop product IDs in current checkout context
 * - isAuthenticated: Required - Boolean if user is logged in
 * - customerInfo: Required - { firstName, lastName, email, phone }
 * - onBookingComplete: Required - Callback(bookingData) when booking is reserved
 * - onCancel: Optional - Callback when user cancels
 */
const InlineBookingWidget = ({
  shopId,
  shopName,
  bookingProductIds = [],
  isAuthenticated,
  customerInfo,
  onBookingComplete,
  onCancel,
}) => {
  const [step, setStep] = useState(1); // 1: Select professional/service, 2: Select date/time
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingShopId, setBookingShopId] = useState(shopId);
  const [singleProfessionalCheckout, setSingleProfessionalCheckout] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  // F2: per-unit quantity / hourly length / chosen option for the selected
  // service. Empty for fixed-price services (the selector renders nothing).
  const [serviceSelection, setServiceSelection] = useState({});

  // F2: the engine owns price AND appointment length. `resolveServicePricing`
  // mirrors the server resolver, so what the customer sees here is what the
  // server re-resolves and charges.
  const resolvedLine = selectedService
    ? resolveServicePricing(selectedService, serviceSelection)
    : null;
  const selectionPending = Boolean(resolvedLine?.requiresSelection);
  const resolvedDuration =
    Number(resolvedLine?.totalMinutes) ||
    Number(selectedService?.durationMinutes) ||
    0;
  const selectedServiceId = selectedService?._id || null;

  // Changing the selection changes the appointment length, and changing the
  // service replaces what is being booked - either way a previously chosen slot
  // was chosen for something else and must be re-picked.
  useEffect(() => {
    setSelectedSlot(null);
  }, [resolvedDuration, selectedServiceId]);

  // Fetch services and staff on mount
  useEffect(() => {
    if (shopId) {
      fetchServicesAndStaff();
    }
  }, [shopId]);

  const fetchServicesAndStaff = async () => {
    try {
      setLoading(true);
      const baseURL = getApiBaseUrl();
      const query = new URLSearchParams({ shopId: String(shopId) });
      if (Array.isArray(bookingProductIds) && bookingProductIds.length > 0) {
        query.set(
          "productIds",
          bookingProductIds.map((id) => String(id)).join(","),
        );
      }
      const optionsRes = await fetch(
        `${baseURL}/api/bookings/checkout-booking-options?${query.toString()}`,
      );
      if (!optionsRes.ok) {
        throw new Error("Failed to fetch booking data");
      }

      const optionsData = await optionsRes.json();
      const activeServices = (optionsData.services || []).filter(
        (s) => s.isActive !== false,
      );
      const activeStaff = optionsData.staff || [];
      setBookingShopId(optionsData.bookingShopId || shopId);
      setSingleProfessionalCheckout(
        Boolean(optionsData.singleProfessionalCheckout) ||
          activeStaff.length === 1,
      );
      setServices(activeServices);
      setStaff(activeStaff);

      // Only auto-select professional when there is exactly one.
      if (activeStaff.length === 1) setSelectedStaff(activeStaff[0]);
    } catch (err) {
      setError("Unable to load booking options");
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    // Selection belongs to the previously selected service.
    setServiceSelection({});
    setError(null);
  };

  const handleStaffSelect = (staffMember) => {
    setSelectedStaff(staffMember);
    // Professional-first flow: selecting/changing professional resets service.
    setSelectedService(null);
    setSelectedSlot(null);
    setServiceSelection({});
    setError(null);
  };

  const handleContinueToCalendar = () => {
    if (!selectedService) {
      setError("Please select a service");
      return;
    }
    if (!selectedStaff) {
      setError("Please select a professional");
      return;
    }
    if (selectionPending) {
      setError("Please choose an option to continue");
      return;
    }
    setStep(2);
  };

  const handleSlotSelect = (selection) => {
    setSelectedSlot(selection);
  };

  const handleReserveBooking = async () => {
    if (!selectedSlot) {
      setError("Please select a date and time");
      return;
    }

    // Phase C11.5: Customer info comes from parent (already validated)
    const token = localStorage.getItem("ogestyle_token");

    try {
      setLoading(true);
      setError(null);

      const baseURL = getApiBaseUrl();
      const headers = token
        ? getAuthHeaders()
        : { "Content-Type": "application/json" };
      const sessionId = getOrCreateSessionId();
      // Prepare booking data
      const bookingData = {
        shopId: bookingShopId,
        staffId: selectedStaff._id,
        serviceId: selectedService._id,
        appointmentDate: selectedSlot.date,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        duration: resolvedDuration || selectedService.durationMinutes,
        // F2: the server re-resolves this selection and owns the final amount.
        selection: serviceSelection,
        status: "reserved", // Will be confirmed after payment
        sessionId, // Link to cart for cleanup/release
      };

      // Add customer info (guest or authenticated)
      if (!isAuthenticated) {
        bookingData.guestInfo = customerInfo;
        bookingData.isGuest = true;
      }

      // Reserve the booking
      const response = await fetch(`${baseURL}/api/bookings/reserve`, {
        method: "POST",
        headers,
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to reserve booking");
      }

      const {
        bookingData: validatedBookingData,
        reservationExpiresAt,
        servicePrice,
        serviceName,
      } = await response.json();

      setBookingSuccess(true);
      // Notify parent component with validated booking data
      if (onBookingComplete) {
        onBookingComplete({
          bookingData: validatedBookingData, // Full booking data to pass to Stripe
          serviceName: serviceName || selectedService?.name,
          servicePrice: servicePrice || selectedService?.price,
          stripeTaxCode: selectedService?.stripeTaxCode || "txcd_00000000",
          bookingChargeType: "fresh_checkout",
          appointmentDate: validatedBookingData.appointmentDate,
          startTime: validatedBookingData.startTime,
          reservationExpiresAt:
            validatedBookingData.reservationExpiresAt || reservationExpiresAt,
          customerName: validatedBookingData.customerName,
        });
      }
    } catch (err) {
      setError(err.message || "Unable to reserve booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setSelectedSlot(null);
    }
  };

  const servicesForStaff = selectedStaff
    ? services.filter(
        (service) =>
          service.staffId?.toString() === selectedStaff._id?.toString(),
      )
    : [];
  const visibleServices = servicesForStaff;

  // F2: per-unit, hourly and option-based services are priced from a customer
  // selection, so the card must not advertise the catalog default as if it were
  // the price. Describe how the service is priced and let the selector below
  // collect the choice.
  const describeService = (service) => {
    const line = resolveServicePricing(service, {});
    if (!line) {
      return `${service.durationMinutes} min • $${service.price}`;
    }
    if (line.pricingType === "hourly") {
      return `$${line.unitPrice} per hour • min ${line.unitMinutes} min`;
    }
    if (line.pricingType === "per_unit") {
      return `$${line.unitPrice} per ${line.unitLabel || "unit"}`;
    }
    if (line.pricingType === "range") {
      const options = line.selectionOptions || [];
      const prices = options
        .map((option) => Number(option.price) || 0)
        .filter((price) => price > 0);
      if (prices.length > 0) {
        return `from $${Math.min(...prices)} • ${options.length} options`;
      }
      return `${line.unitMinutes} min`;
    }
    return `${line.totalMinutes} min • $${line.totalPrice}`;
  };

  // Phase C11.5: Removed auth handlers - CheckoutPage handles all auth

  // Helper to format YYYY-MM-DD date string to readable format (avoids UTC conversion)
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (bookingSuccess) {
    return (
      <div className="inline-booking-success">
        <div className="success-icon">✓</div>
        <h3>Service Reserved!</h3>
        <p>Your appointment has been added to this order.</p>
        <div className="booking-summary">
          <p>
            <strong>{resolvedLine?.label || selectedService.name}</strong>
          </p>
          {resolvedLine ? (
            <p>
              {resolvedDuration} minutes • ${resolvedLine.totalPrice}
            </p>
          ) : null}
          <p>
            with {selectedStaff.firstName} {selectedStaff.lastName}
          </p>
          <p>
            📅 {formatDate(selectedSlot.date)} at {selectedSlot.startTime}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="inline-booking-widget">
      <div className="inline-booking-header">
        <h3>📅 Book a Service at {shopName}</h3>
        {onCancel && (
          <button className="close-widget-btn" onClick={onCancel}>
            ✕
          </button>
        )}
      </div>

      {error && <div className="inline-booking-error">⚠️ {error}</div>}

      {step === 1 && (
        <div className="step-1-selection">
          {/* Staff Selection */}
          {!singleProfessionalCheckout && (
            <div className="selection-section">
              <label className="selection-label">Select Professional:</label>
              {loading ? (
                <div className="loading-placeholder">
                  Loading professionals...
                </div>
              ) : staff.length === 0 ? (
                <p className="no-options">No professionals available</p>
              ) : (
                <div className="options-grid">
                  {staff.map((staffMember) => (
                    <div
                      key={staffMember._id}
                      className={`option-card ${selectedStaff?._id === staffMember._id ? "selected" : ""}`}
                      onClick={() => handleStaffSelect(staffMember)}
                    >
                      <div className="option-name">
                        {staffMember.firstName && staffMember.lastName
                          ? `${staffMember.firstName} ${staffMember.lastName}`
                          : staffMember.name || "Professional"}
                      </div>
                      {staffMember.professionalType && (
                        <div className="option-details">
                          {formatProfessionalType(staffMember.professionalType)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {singleProfessionalCheckout && selectedStaff && (
            <div className="selection-section">
              <label className="selection-label">Professional:</label>
              <div className="option-card selected">
                <div className="option-name">
                  {selectedStaff.firstName && selectedStaff.lastName
                    ? `${selectedStaff.firstName} ${selectedStaff.lastName}`
                    : selectedStaff.name || "Professional"}
                </div>
                {selectedStaff.professionalType && (
                  <div className="option-details">
                    {formatProfessionalType(selectedStaff.professionalType)}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Service Selection */}
          <div className="selection-section">
            <label className="selection-label">Select Service:</label>
            {loading ? (
              <div className="loading-placeholder">Loading services...</div>
            ) : !selectedStaff ? (
              <p className="no-options">Select a professional first</p>
            ) : visibleServices.length === 0 ? (
              <p className="no-options">
                No services available for this professional
              </p>
            ) : (
              <div className="options-grid">
                {visibleServices.map((service) => (
                  <div
                    key={service._id}
                    className={`option-card ${selectedService?._id === service._id ? "selected" : ""}`}
                    onClick={() => handleServiceSelect(service)}
                  >
                    <div className="option-name">{service.name}</div>
                    <div className="option-details">
                      {describeService(service)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* F2: pricing selection for per-unit / hourly / option services */}
          {selectedService && !loading && (
            <div className="selection-section">
              <ServicePricingSelector
                service={selectedService}
                selection={serviceSelection}
                onChange={(next) =>
                  setServiceSelection((prev) => ({ ...prev, ...next }))
                }
              />
            </div>
          )}

          {/* What the customer will actually be charged and how long it takes */}
          {selectedService && resolvedLine && !selectionPending && (
            <div className="selected-info">
              <p>
                <strong>{resolvedLine.label}</strong>
              </p>
              <p className="service-duration">
                {resolvedDuration} minutes • ${resolvedLine.totalPrice}
              </p>
            </div>
          )}

          <button
            className="continue-btn"
            onClick={handleContinueToCalendar}
            disabled={!selectedService || !selectedStaff || selectionPending}
          >
            Continue to Select Date & Time →
          </button>
        </div>
      )}

      {step === 2 && selectedService && selectedStaff && (
        <div className="step-2-calendar">
          <button className="back-btn" onClick={handleBack}>
            ← Back to Selection
          </button>

          <div className="selected-info">
            <p>
              <strong>{resolvedLine?.label || selectedService.name}</strong> with{" "}
              {selectedStaff.firstName} {selectedStaff.lastName}
            </p>
            <p className="service-duration">
              {resolvedDuration} minutes
              {resolvedLine ? ` • $${resolvedLine.totalPrice}` : ""}
            </p>
          </div>

          <AvailabilityCalendar
            staffId={selectedStaff._id}
            locationId={bookingShopId}
            serviceDuration={resolvedDuration || selectedService.durationMinutes}
            onSlotSelect={handleSlotSelect}
          />

          {selectedSlot && (
            <div className="slot-confirmation">
              <p className="confirmation-text">
                Selected: {formatDate(selectedSlot.date)} at{" "}
                {selectedSlot.startTime}
              </p>
              <button
                className="reserve-btn"
                onClick={handleReserveBooking}
                disabled={loading}
              >
                {loading ? "Adding..." : "Add Service To This Order"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Phase C11.5: Step 3 removed - guest info handled by CheckoutPage */}
    </div>
  );
};

export default InlineBookingWidget;
